import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Offcanvas } from 'reactstrap';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { IconsButton } from 'components/buttons/icons-button/icons-button';
import { WaitingLoader } from 'components/loaders/waiting-loader/waiting-loader';
import { Panel } from 'components/panels/panel';
import { PanelThemeType, PanelType } from 'components/panels/panel-type';
import { AppContext } from 'core/context/app.context';
import { CsatActions, GenexActions } from 'core/enums/genex';
import { usePlatform } from 'core/hooks/platform';
import { GenexStore, ModelWithCategory } from 'core/stores/genex.store';
import { Util } from 'utils/common.utils';
import { LiveChatPages, LiveChatStore } from './live-chat.store';
import EndChat from './steps/end-chat';
import ErrorMessageScreen from './steps/error-message';
import LiveChatScreen from './steps/live-chat-screen';
import styles from './live-chat.module.scss';

export const LiveChatLegacy = observer(({ shouldOpen, store }: { shouldOpen?: boolean; store: any }) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation();
  const { platformKey, genexActionCode } = usePlatform();

  useEffect(() => {
    store.initialize(appContext.customerNumber, appContext.countryName, shouldOpen);
  }, [t, appContext, store]);

  const handleClose = () => {
    store.disconnectChat();

    const surveyModel = {
      category: GenexActions.LiveChat,
      outcome: 'Success',
      surveyName: genexActionCode(CsatActions.LiveChat),
    } as ModelWithCategory;

    GenexStore.saveUserData({
      ...GenexStore.optionalUserData,
      smartCardNumber: store.smartcardNumber || '',
    });
    GenexStore.triggerSurvey(surveyModel, appContext.customerNumber, new Date());
  };

  const initiateChat = async () => {
    store.startChat();
    store.changeIsChatOpen(true);
  };

  const sendMessage = async (message: string) => {
    store.sendMessage(message);
  };

  const handleCloseClick = () => {
    if (store.hub) {
      store.changeActivePage(LiveChatPages.EndChat);
      return;
    }
    store.changeIsChatOpen(false);
  };

  const getMarginClass = () => {
    return store.activePage !== LiveChatPages.LiveChat || store.isLoading
      ? styles.autoMargin
      : styles.bodyContainer;
  };

  const getDisplayName = () => {
    return (
      <>
        <svg width="16" height="16" viewBox="0 0 16 16">
          <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <path
              d="M16,2 
              L16,10.4444444 
              C16,11.5490139 15.1045695,12.4444444 
              14,12.4444444 L11.9109137,12.4444444 
              L11.9109137,16 L7.74039,12.4444444 
              L2,12.4444444 C0.8954305,12.4444444 
              0,11.5490139 0,10.4444444 
              L0,2 C0,0.8954305 0.8954305,0 2,0 L14,0 C15.1045695,0 16,0.8954305 16,2 Z"
              id="chat"
              fill="#FFFFFF"
            ></path>
          </g>
        </svg>
        <span className={`cust-ml-1`}>{t('liveChat.buttonTitle')}</span>
      </>
    );
  };

  return (
    <Panel backgroundType={PanelThemeType.Transparent} sizeType={PanelType.FullWidth}>
      <div className={styles.liveChat}>
        {!store.isChatOpen && !store.isLoading && (
          <IconsButton
            meta={{
              name: t('liveChat.buttonTitle'),
              title: t('liveChat.buttonTitle'),
            }}
            classes={['cust-ml-1', 'btn', styles.liveChatButton]}
            onClick={initiateChat}
          >
            {getDisplayName()}
          </IconsButton>
        )}
        <Offcanvas direction="bottom" isOpen={store.isChatOpen} className={styles.canvasContainer}>
          <div className={`d-flex justify-content-between ${styles.liveChatHeaderContainer}`}>
            <span>
              {store.activePage === LiveChatPages.LiveChat && !store.isLoading && (
                <img
                  height={36}
                  width={107}
                  src={Util.GetImageAsset(`${platformKey}-live-agent.png`)}
                  alt={'Live agent Logo'}
                />
              )}
            </span>
            <span onClick={handleCloseClick}>
              <i className="icon-close"></i>
            </span>
          </div>

          <div className={`${getMarginClass()}`}>
            <WaitingLoader
              titles={[t('liveChat.loaderTitle', { name: store?.user?.firstName })]}
              texts={[t('liveChat.loaderDescription')]}
              isLoading={store.isLoading}
            >
              {
                {
                  [LiveChatPages.LiveChat]: (
                    <LiveChatScreen
                      sendMessage={sendMessage}
                      messages={store.messages}
                      latestEvent={store.latestEvent}
                      isConnected={!!store.hub}
                      handleFeedback={handleClose}
                      styles={styles}
                    />
                  ),
                  [LiveChatPages.EndChat]: <EndChat handleChatEnd={handleClose} localStore={store} styles={styles} />,
                  [LiveChatPages.Error]: <ErrorMessageScreen retry={initiateChat} styles={styles} />,
                }[store.activePage as LiveChatPages]
              }
            </WaitingLoader>
          </div>
        </Offcanvas>
      </div>
    </Panel>
  );
});